/**
 * Historial de conversaciones con un asistente IA del admin, por código de agente (la Guía de
 * cada portal, el Analista de tickets, ...). El Agente CEO tiene su propia lectura (compartidas
 * con Dirección, archivadas) en `useAgenteCeo` y usa los mismos componentes.
 *
 * Las funciones de cada agente guardan cada turno en `ia_conversaciones` / `ia_mensajes`; aquí
 * solo se leen (RLS: cada quien ve las suyas) y se cambian por la RPC `ia_actualizar_conversacion`
 * (renombrar, fijar, borrar = `estado 'eliminada'`), que valida que sean del propio usuario.
 * Nada se escribe por tabla.
 *
 * `ia_agentes` no es legible para los roles de los portales (su policy es de Dirección), así que
 * el id del agente sale de `ia_agente_id(codigo)`, que es SECURITY DEFINER. La lista siempre
 * filtra por agente y por usuario: la tabla la comparten todos los agentes y la policy deja ver
 * las conversaciones compartidas del CEO a quien tenga ese permiso.
 */
import { useCallback } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { mensajesIaDesdeBitacora, normalizarConversacionesIa, type ConversacionIa, type MensajeIa } from "@/lib/chatIa";

/** Tope de la lista: suficiente para meses de uso sin paginar. */
const MAX_CONVERSACIONES = 100;

export const claveHistorialChatIa = (usuario: string, agente: string) => ["chat-ia-historial", usuario, agente] as const;

export interface CambioConversacion {
  id: string;
  titulo?: string;
  fijada?: boolean;
  eliminar?: boolean;
}

export function useHistorialChatIa(agente: string, { habilitado }: { habilitado: boolean }) {
  const { user } = useAuth();
  const uid = user?.id ?? null;
  const qc = useQueryClient();
  const clave = claveHistorialChatIa(uid ?? "anon", agente);

  const lista = useQuery<ConversacionIa[]>({
    queryKey: clave,
    enabled: habilitado && !!uid,
    staleTime: 30_000,
    retry: false,
    queryFn: async () => {
      const { data: idAgente, error: errId } = await (supabase as any).rpc("ia_agente_id", { p_codigo: agente });
      if (errId) throw new Error(errId.message);
      if (idAgente == null) return [];
      const { data, error } = await (supabase as any)
        .from("ia_conversaciones")
        .select("id, numero, titulo, fijada, fecha_ultimo_mensaje, fecha_creacion")
        .eq("id_agente", idAgente)
        .eq("id_usuario", uid)
        .eq("estado", "activa")
        .eq("es_prueba", false)
        .order("fecha_ultimo_mensaje", { ascending: false, nullsFirst: false })
        .limit(MAX_CONVERSACIONES);
      if (error) throw new Error(error.message);
      return normalizarConversacionesIa(data);
    },
  });

  const cargarMensajes = useCallback(async (idConversacion: string): Promise<MensajeIa[]> => {
    const { data, error } = await (supabase as any)
      .from("ia_mensajes")
      .select("id, rol, contenido, error, orden, herramientas")
      .eq("id_conversacion", idConversacion)
      .order("orden", { ascending: true });
    if (error) throw new Error(error.message);
    return mensajesIaDesdeBitacora(data);
  }, []);

  const actualizar = useMutation({
    mutationFn: async (v: CambioConversacion) => {
      const { error } = await (supabase as any).rpc("ia_actualizar_conversacion", {
        p_id: v.id,
        p_titulo: v.titulo ?? null,
        p_fijada: v.fijada ?? null,
        p_visibilidad: null,
        p_estado: v.eliminar ? "eliminada" : null,
      });
      if (error) throw new Error(error.message);
    },
    // Optimista: la lista responde al instante; si la RPC falla, se vuelve a leer.
    onMutate: (v) => {
      qc.setQueryData<ConversacionIa[]>(clave, (prev) =>
        (prev ?? [])
          .filter((c) => !(v.eliminar && c.id === v.id))
          .map((c) => (c.id === v.id ? { ...c, titulo: v.titulo?.trim() || c.titulo, fijada: v.fijada ?? c.fijada } : c)),
      );
    },
    onSettled: () => qc.invalidateQueries({ queryKey: clave }),
  });

  /** Tras un turno: conversación nueva o fecha del último mensaje movida. */
  const refrescar = useCallback(
    () => qc.invalidateQueries({ queryKey: claveHistorialChatIa(uid ?? "anon", agente) }),
    [qc, uid, agente],
  );

  return {
    conversaciones: lista.data ?? [],
    cargando: lista.isLoading,
    error: lista.isError,
    recargar: lista.refetch,
    refrescar,
    cargarMensajes,
    actualizar,
  };
}
